import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';

import styles from './index.module.scss';

import ConfirmSlider from '@/components/ui/ConfirmSlider';
import Spinner from '@/components/ui/Spinner';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { sleep } from '@/utils/Sleep';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// 상수 정의
const MOTOR_NUMBERS = [1, 2, 3, 4];
const MOTOR_PWM = 1100;
const SURFACE_NUMBERS = [1, 2];
const SURFACE_PWM_MIN = 1100;
const SURFACE_PWM_MAX = 1800;
const TIMEOUT = 3;

const Panel = ({ data: robots }) => {
  const [isProcessing, setProcessing] = useState(false);
  const [currentTest, setCurrentTest] = useState();
  const surfaceDefaultTrim = useRef({});
  const testTarget = useRef();

  const actionCommand = useActionCommand();
  const paramCommand = useParamCommand();

  useMountEffect(() => {
    robots.forEach((robot) => {
      surfaceDefaultTrim.current[robot.id] = {
        SERVO1_TRIM: Number(robot.metadata['SERVO1_DEFAULT_TRIM']),
        SERVO2_TRIM: Number(robot.metadata['SERVO2_DEFAULT_TRIM']),
      };
    });
  });

  //#region Process Functions
  const process = async (robot) => {
    try {
      // 전체 테스트
      if (testTarget.current === 'all') {
        // 모터 1번
        setCurrentTest({ type: 'motor', number: 1 });
        await processMotor(robot, 1);

        // 모터 2번
        setCurrentTest({ type: 'motor', number: 2 });
        await processMotor(robot, 2);

        // 모터 3번
        setCurrentTest({ type: 'motor', number: 3 });
        await processMotor(robot, 3);

        // 모터 4번
        setCurrentTest({ type: 'motor', number: 4 });
        await processMotor(robot, 4);

        // 우측 타면
        setCurrentTest({ type: 'surface', number: 1 });
        await processSurface(robot, 'SERVO1_TRIM');

        // 좌측 타면
        setCurrentTest({ type: 'surface', number: 2 });
        await processSurface(robot, 'SERVO2_TRIM');
      }
      // 개별 테스트
      else {
        setCurrentTest(testTarget.current);

        // 모터 테스트
        if (testTarget.current.type === 'motor') {
          await processMotor(robot, testTarget.current.number);
        }
        // 타면 테스트
        else if (testTarget.current.type === 'surface') {
          let paramId;
          if (testTarget.current.number === 1) {
            paramId = 'SERVO1_TRIM';
          } else if (testTarget.current.number === 2) {
            paramId = 'SERVO2_TRIM';
          }

          await processSurface(robot, paramId);
        }
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processMotor = async (robot, motorNumber) => {
    await executeMavLinkCommand(robot, actionCommand.motorTest(motorNumber, 1, MOTOR_PWM, TIMEOUT));
    await sleep(TIMEOUT * 1000 + 1000); // 모터 테스트 실행 후 대기
  };

  const processSurface = async (robot, paramId) => {
    await executeMavLinkCommand(robot, paramCommand.set(paramId, SURFACE_PWM_MIN, 4));
    await sleep(1000);
    await executeMavLinkCommand(robot, paramCommand.set(paramId, SURFACE_PWM_MAX, 4));
    await sleep(1000);
    await executeMavLinkCommand(robot, paramCommand.set(paramId, surfaceDefaultTrim.current[robot.id][paramId], 4));
    await sleep(1000);
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.success(`Inspection completed successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.error(`Inspection failed on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doStartAll = () => {
    testTarget.current = 'all';
    setProcessing(true);
  };

  const doStartSingle = (e) => {
    const type = e.currentTarget.dataset.type;
    const number = Number(e.currentTarget.dataset[type]);

    testTarget.current = { type, number };
    setProcessing(true);
  };

  const doReset = (e) => {
    const key = e.currentTarget.dataset.key;

    robots.forEach((robot) => {
      executeMavLinkCommand(robot, paramCommand.set(key, surfaceDefaultTrim.current[robot.id][key], 4));
    });
  };

  const getActive = (type, number) => {
    return currentTest?.type === type && currentTest?.number === number;
  };

  return (
    <div className={cx('container')}>
      <div className={cx('figure')}>
        <img src={require('./inspection-body.png')} alt="" className={cx('body')} />
        {/* Motor #1 */}
        <div className={cx('motor', 'motor1')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive('motor', MOTOR_NUMBERS[0]) }])}
          />
          <div className={cx('info')}>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #1</div>
              <button data-motor={1} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[0]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[0]) && <Spinner size={14} />}
              </button>
            </div>
            <svg width="60" height="40">
              <line
                x1="36"
                y1="0"
                x2="60"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
          </div>
        </div>
        {/* Motor #2 */}
        <div className={cx('motor', 'motor2')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', { active: getActive('motor', MOTOR_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="60" height="40">
              <line
                x1="60"
                y1="0"
                x2="36"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #2</div>
              <button data-motor={2} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[1]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[1]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #3 */}
        <div className={cx('motor', 'motor3')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive('motor', MOTOR_NUMBERS[2]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="40">
              <line
                x1="12"
                y1="0"
                x2="36"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #3</div>
              <button data-motor={3} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[2]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[2]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #4 */}
        <div className={cx('motor', 'motor4')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', { active: getActive('motor', MOTOR_NUMBERS[3]) }])}
          />
          <div className={cx('info')}>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #4</div>
              <button data-motor={4} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[3]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[3]) && <Spinner size={14} />}
              </button>
            </div>
            <svg width="36" height="40">
              <line
                x1="36"
                y1="0"
                x2="12"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
          </div>
        </div>
        {/* Wing Right */}
        <div className={cx('wing', 'right')}>
          <img
            src={require('./inspection-surface-right.png')}
            alt=""
            className={cx(['surface', { active: getActive('surface', SURFACE_NUMBERS[0]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="36">
              <line
                x1="24"
                y1="0"
                x2="36"
                y2="64"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (R)</div>
              <button data-surface={1} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[0]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[0]) && <Spinner size={14} />}
              </button>
              <button data-key="SERVO1_TRIM" onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
        {/* Wing Left */}
        <div className={cx('wing', 'left')}>
          <img
            src={require('./inspection-surface-left.png')}
            alt=""
            className={cx(['surface', { active: getActive('surface', SURFACE_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="48" height="36">
              <line
                x1="48"
                y1="0"
                x2="36"
                y2="64"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (L)</div>
              <button data-surface={2} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[1]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[1]) && <Spinner size={14} />}
              </button>
              <button data-key="SERVO2_TRIM" onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmSlider onConfirm={doStartAll} isProcessing={isProcessing} />
    </div>
  );
};

export default Panel;
